import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { ItemsContext_OrderGuideItemFieldsFragment } from '@/types';
import {
  MemoRow,
  NoteContainer,
  RequestButton,
} from '@/components/note-elements';
import { NoteType } from '@/enums';
import { TouchableOpacity } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useCanWriteLineItemRequest } from '@/hooks/dynamic-config/use-can-write-line-item-request';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';

interface Props {
  onPress: (type?: NoteType) => void;
  orderGuideItem: ItemsContext_OrderGuideItemFieldsFragment;
}

export const Note: React.FC<Props> = ({ onPress, orderGuideItem }) => {
  const isOrderNoteEnabled = useSupplierDynamicConfigValue(
    'line_item_notes_config',
  )?.enabled;
  const canWriteRequest = useCanWriteLineItemRequest();

  const [styles] = useStyles();

  const { memo, notes, request } = orderGuideItem.order_guide_restaurant || {};
  if (isOrderNoteEnabled) {
    return memo || request ? (
      <NoteContainer>
        {memo ? (
          <MemoRow
            onPress={() => {
              Analytics.track('order_guide_list_item.edit_note_button', {
                orderGuideItemUUID: orderGuideItem.order_guide_item_id,
                type: NoteType.Memo,
                variantPackUUID: orderGuideItem.variant_pack?.uuid,
              });

              onPress(NoteType.Memo);
            }}
          >
            {memo}
          </MemoRow>
        ) : null}
        {request && canWriteRequest ? (
          <RequestButton
            accessibilityLabel={i18n.t('order_guide_item.request_note')}
            onPress={() => {
              Analytics.track('order_guide_list_item.edit_note_button', {
                orderGuideItemUUID: orderGuideItem.order_guide_item_id,
                type: NoteType.Request,
                variantPackUUID: orderGuideItem.variant_pack?.uuid,
              });

              onPress(NoteType.Request);
            }}
          />
        ) : null}
      </NoteContainer>
    ) : null;
  }

  return notes ? (
    <TouchableOpacity
      accessibilityRole="button"
      focusable={false}
      onPress={() => {
        Analytics.track('order_guide_list_item.edit_note_button', {
          orderGuideItemUUID: orderGuideItem.order_guide_item_id,
          variantPackUUID: orderGuideItem.variant_pack?.uuid,
        });

        onPress();
      }}
      style={styles.legacyNoteContainer}
    >
      <Typography numberOfLines={1} variant="paragraph-sm">
        {orderGuideItem.order_guide_restaurant?.notes}
      </Typography>
    </TouchableOpacity>
  ) : null;
};

const useStyles = createStyles((theme) => ({
  legacyNoteContainer: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.highlight.tertiary,
    borderRadius: theme.sk.radii.radiusSmall,
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    padding: theme.sk.spacing.spaceSmall,
  },
}));
